import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LucideIcon } from "lucide-react";

interface Benefit {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  percentage: string;
  description: string;
  metrics: string[];
  color: string;
  gradient: string;
}

interface BenefitsSectionProps {
  benefits: Benefit[];
}

const BenefitsSection: React.FC<BenefitsSectionProps> = ({ benefits }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  
  return (
    <div ref={sectionRef} className="relative pt-32 pb-24 overflow-hidden bg-gradient-to-br from-blue-500/5 via-purple-500/10 to-cyan-500/5">
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ y: backgroundY }}
      >
        <div className="absolute inset-0 " />
        <motion.div
          className="absolute top-20 left-20 w-96 h-96 bg-green-400/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-20 right-20 w-80 h-80 bg-purple-400/10 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.6, 0.3, 0.6]
          }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </motion.div>

      <motion.div 
        className="text-center mb-20 relative z-10"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false, margin: "0%" }}
      >
        <motion.div
          className="relative inline-block mb-6"
          initial={{ scale: 0.9 }}
          whileInView={{ scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: false }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 via-blue-400/20 to-purple-400/20 rounded-3xl blur-2xl" />
          <h2 className="relative text-6xl lg:text-7xl font-black text-white leading-tight px-8 py-4">
            Transformative
            <br />
            <span className="bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              Business Impact
            </span>
          </h2>
        </motion.div>
        
        <motion.p 
          className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: false }}
        >
          Experience revolutionary efficiency gains that redefine what's possible in software development
        </motion.p>
      </motion.div>

      <div className="max-w-8xl mx-auto px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            
            return (
              <motion.div
                key={`benefit-${index}`}
                className="relative group"
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: false, margin: "0%" }}
              >
                <motion.div 
                  className="relative bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-3xl p-8 h-full overflow-hidden"
                  whileHover={{ 
                    scale: 1.02,
                    rotateX: 2,
                    rotateY: 2
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    boxShadow: `0 20px 40px ${benefit.color}15`
                  }}
                >
                  <motion.div 
                    className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-500`}
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 0.05 }}
                    whileHover={{ opacity: 0.1 }}
                    transition={{ duration: 0.6 }}
                  />

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                      <motion.div 
                        className="flex items-center space-x-4"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 + 0.2 }}
                        viewport={{ once: false }}
                      >
                        <div 
                          className="p-4 rounded-2xl"
                          style={{ backgroundColor: `${benefit.color}20` }}
                        >
                          <Icon 
                            className="w-8 h-8" 
                            style={{ color: benefit.color }}
                          />
                        </div>
                      </motion.div>
                      
                      <motion.div 
                        className="text-right"
                        initial={{ opacity: 0, scale: 0.5 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: index * 0.1 + 0.3 }}
                        viewport={{ once: false }}
                      >
                        <div 
                          className="text-5xl font-black leading-none"
                          style={{ color: benefit.color }}
                        >
                          {benefit.percentage}
                        </div>
                        <div className="text-sm text-gray-400 font-medium mt-1">
                          {benefit.percentage === "100%" ? "QUALITY" : benefit.percentage === "5x" ? "EFFICIENCY" : benefit.percentage === "10x" ? "FASTER" : "SAVINGS"}
                        </div>
                      </motion.div>
                    </div>

                    <motion.div 
                      className="mb-4"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 + 0.4 }}
                      viewport={{ once: false }}
                    >
                      <h3 className="text-3xl font-black text-white mb-2 leading-tight">
                        {benefit.title}
                      </h3>
                      <p className="text-lg font-medium" style={{ color: benefit.color }}>
                        {benefit.subtitle}
                      </p>
                    </motion.div>

                    <motion.p 
                      className="text-gray-300 leading-relaxed mb-6"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 + 0.5 }}
                      viewport={{ once: false }}
                    >
                      {benefit.description}
                    </motion.p>

                    <motion.div 
                      className="space-y-3"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.6, delay: index * 0.1 + 0.6 }}
                      viewport={{ once: false }}
                    >
                      {benefit.metrics.map((metric: string, i: number) => (
                        <motion.div 
                          key={`metric-${index}-${i}`}
                          className="flex items-center space-x-3"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 + 0.7 + i * 0.1 }}
                          viewport={{ once: false }}
                        >
                          <motion.div 
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: benefit.color }}
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            transition={{ duration: 0.3, delay: index * 0.1 + 0.8 + i * 0.1 }}
                            viewport={{ once: false }}
                          />
                          <span className="text-gray-300 text-sm">{metric}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>

                  <motion.div
                    className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 pointer-events-none"
                    style={{
                      background: `linear-gradient(135deg, ${benefit.color}10, transparent 70%)`,
                      boxShadow: `inset 0 0 60px ${benefit.color}20`
                    }}
                    transition={{ duration: 0.5 }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BenefitsSection;